import React, { useEffect, useMemo, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import "rc-slider/assets/index.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useMount } from "react-use";

import CarouselOrMosaicForm from "./CarouselOrMosaicForm";
import { GET_SMARTLISTS } from "../../actions/ActionTypes";
import { postApi } from "../../actions/SendActions";
import {
  createComponent,
  updateComponent,
} from "../../api/legacy/ComponentAPI";
import ProjectAPI from "../../api/ProjectAPI";
import { SECTION } from "../../api/ProjectAPI.types";
import Breadcrumbs from "../../components/utils/Breadcrumbs";
import ContainerHeader from "../../components/utils/ContainerHeader";
import Spinner from "../../components/utils/Spinner";
import { COMPONENT_TYPES } from "../../constants/componentType";
import { IMAGE_FORMATS } from "../../constants/ratio";
import { getElementByUuid } from "../../utils/data/Content";
import ItemStatus from "../../utils/ItemStatus";

import type { JSX } from "react";

import type { SmartlistsState } from "@/src/reducers/fetch.types";

import type {
  CarouselComponent,
  GetProjectPageComponentListResponse,
  GetProjectPageResponse,
  MosaicComponentWithoutIndividualCarousel,
} from "../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../components/routes/PlatformRoutes";
import type { AppState } from "../../reducers";

const containerHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097766-the-smart-components-editor-of-your-homepage",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097766-l-editeur-des-smart-composants-de-votre-page-d-accueil",
};

const { MOSAIC } = COMPONENT_TYPES;
const { PAGE } = SECTION;

type MosaicOrCarouselComponent =
  | MosaicComponentWithoutIndividualCarousel
  | CarouselComponent;

interface PlatformComponentCarouselOrMosaicProps {
  initialComponentType: "mosaic" | "carousel";
}

function PlatformComponentCarouselOrMosaic({
  initialComponentType,
}: PlatformComponentCarouselOrMosaicProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const dispatch = useDispatch();
  const history = useHistory();
  const { projectSlug, platform, componentId, pageId } =
    useParams<PlatformRoutesParams>();

  const queryClient = useQueryClient();
  const smartlists = useSelector(
    (state: AppState) => state.fetchReducer.smartlists,
  );
  const token = useSelector((state: AppState) => state.loginReducer.token);

  const isNew = componentId === "new";

  const getComponentQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getComponent<
      CarouselComponent | MosaicComponentWithoutIndividualCarousel
    >,
    params: [{ projectSlug, componentId }],
    options: {
      enabled: !isNew,
    },
  });

  const {
    data: componentData,
    isFetching,
    isFetched,
  } = useQuery(getComponentQuery);

  const getProjectPageComponentListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPageComponentList,
    params: [{ pageId, platform, projectSlug }],
    options: {
      enabled: isNew,
    },
  });

  const { data: projectPageComponentListData } = useQuery({
    ...getProjectPageComponentListQuery,
    select: (data) => data.components,
  });

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
    options: {
      enabled: isNew,
    },
  });

  const { data: projectPageData } = useQuery(getProjectPageQuery);

  const [component, setComponent] = useState<MosaicOrCarouselComponent>();

  useMount(() => {
    if (!smartlists || smartlists.data.length <= 0) {
      dispatch(
        postApi({
          endpoint: "/api/smartlist/v3/admin/smartlists",
          type: GET_SMARTLISTS,
          item: "smartlists",
          token,
          headers: [
            {
              key: "xx-ok-project",
              value: projectSlug,
            },
          ],
          data: {
            nested: {
              smartlist_items: {},
              countries: {},
              translations: {},
            },
            page: 1,
            per_page: 100000,
          },
        }),
      );
    }
  });

  useEffect(() => {
    if (isNew) {
      const baseComponent = {
        section: PAGE,
        platform,
        enabled: false,
        static_component: null,
        dynamic_component: {
          smartlist_uuid: "",
        },
        uuid: "new",
        rank:
          projectPageComponentListData?.length !== undefined
            ? projectPageComponentListData.length + 1
            : 0,
        storefront_project_page_uuid: pageId,
        params: {
          themeId: null,
          display_item_title: false,
          display_title: true,
          display_description: false,
          display_dialog: true,
          ratio: IMAGE_FORMATS.FORMAT_16_9,
        },
      };

      if (initialComponentType === MOSAIC) {
        setComponent({
          ...baseComponent,
          type: "mosaic",
          label: t("newMosaic"),
        });

        return;
      }

      setComponent({
        ...baseComponent,
        type: "carousel",
        label: t("newCarousel"),
        params: {
          ...baseComponent.params,
          arrows_on_hover: true,
          circular: true,
          autoplay: false,
          interval: 1000,
          component_per_slides: 6,
          display_arrows: true,
        },
      });
    }
    if (componentData) {
      setComponent(componentData);
    }
  }, [
    isNew,
    isFetched,
    componentData,
    initialComponentType,
    platform,
    t,
    pageId,
    history,
    projectPageComponentListData?.length,
  ]);

  const handleComponentUpdate = (
    updatedComponent: MosaicOrCarouselComponent,
  ): void => {
    setComponent(updatedComponent);
  };

  const handleChangeTitle = (target: { value: string }): void => {
    setComponent((prevComponent) => {
      if (!prevComponent) return undefined;

      return {
        ...prevComponent,
        label: target.value,
      };
    });
  };

  const handleChangeEnabled = (enabled: boolean): void => {
    setComponent((prevComponent) => {
      if (!prevComponent) return undefined;

      return {
        ...prevComponent,
        enabled,
      };
    });
  };

  const handleSave = (): void => {
    if (!component) return;

    const updatedComponent = { ...component };
    if (
      updatedComponent.dynamic_component &&
      !updatedComponent.dynamic_component.smartlist_uuid
    ) {
      updatedComponent.dynamic_component = null;
    }

    if (updatedComponent.params?.conditional_render?.length) {
      updatedComponent.params.conditional_render =
        updatedComponent.params.conditional_render.filter(
          (condition) =>
            !!(condition.type && condition.operator && condition.predicat),
        );
    }

    const isNewComponent = updatedComponent.uuid === "new";
    const prevPathname = history.location.pathname;
    const updatedPathnameType = prevPathname.replace(
      /\/(mosaic|carousel)/,
      `/${updatedComponent.type}`,
    );

    if (isNewComponent) {
      dispatch(
        createComponent(
          updatedComponent,
          token,
          projectSlug,
          t,
          (newComponent: MosaicOrCarouselComponent) => {
            const newPathnameId = updatedPathnameType.replace(
              /\/(new)$/,
              `/${newComponent.uuid}`,
            );

            queryClient.setQueryData(
              getProjectPageComponentListQuery.queryKey,
              (
                oldData?: GetProjectPageComponentListResponse,
              ): GetProjectPageComponentListResponse | undefined => {
                if (!oldData) return undefined;
                return {
                  ...oldData,
                  components: oldData.components.concat(newComponent),
                };
              },
            );

            const hasPlatformInPage =
              projectPageData?.components_platform?.includes(platform);

            if (!hasPlatformInPage) {
              queryClient.setQueryData<GetProjectPageResponse>(
                getProjectPageQuery.queryKey,
                (oldData) => {
                  if (!oldData) return oldData;
                  return {
                    ...oldData,
                    components_platform: [
                      ...(oldData?.components_platform ?? []),
                      platform,
                    ],
                  };
                },
              );
            }

            history.replace(newPathnameId);
          },
        ),
      );
    } else {
      dispatch(
        updateComponent(
          updatedComponent,
          token,
          projectSlug,
          t,
          (savedComponent: MosaicOrCarouselComponent) => {
            if (initialComponentType !== savedComponent?.type) {
              history.replace(updatedPathnameType);
            }
            queryClient.setQueryData(
              getProjectPageComponentListQuery.queryKey,
              (
                oldData?: GetProjectPageComponentListResponse,
              ): GetProjectPageComponentListResponse | undefined => {
                if (!oldData) return undefined;
                return {
                  ...oldData,
                  components: oldData.components.map((item) =>
                    item.uuid === savedComponent.uuid ? savedComponent : item,
                  ),
                };
              },
            );

            queryClient.setQueryData(
              getComponentQuery.queryKey,
              savedComponent,
            );
          },
        ),
      );
    }
  };

  const smartlist = useMemo(() => {
    if (!component?.dynamic_component?.smartlist_uuid) return undefined;

    const smartlistUuid = component.dynamic_component.smartlist_uuid;

    return (
      getElementByUuid as (uuid: string, data: SmartlistsState) => string
    )(smartlistUuid, smartlists);
  }, [component, smartlists]);

  const componentStatus: string = useMemo(
    () =>
      (
        ItemStatus.getComponentStatus as (
          component?: MosaicOrCarouselComponent,
          smartlist?: string,
        ) => string
      )(component, smartlist),
    [component, smartlist],
  );
  const breadcrumbs = [
    {
      name: t("editor-menu:platform"),
      url: "/layout",
    },
    {
      name: t("title.layout"),
      url: "/layout",
    },
    {
      name: t(`editor-platform:page`),
      url: `/layout/pages/${pageId}/${platform}`,
    },
    {
      name: t(`${component?.type}`),
    },
  ];

  return (
    <div
      className="editor-container editor-has-breadcrumbs"
      data-testid="PlatformComponentCarouselOrMosaic"
    >
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        infoLinks={containerHeaderLinks}
        itemStatus={componentStatus}
        onChangeLabel={isFetching ? undefined : handleChangeTitle}
        onChangeState={isFetching ? undefined : handleChangeEnabled}
        onSave={isFetching ? undefined : handleSave}
        item={{
          label: isFetching ? "..." : component?.label,
        }}
      />
      <div className="editor-tile-container">
        {isFetching ? (
          <div className="flex h-[1000px] items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <CarouselOrMosaicForm
            component={component}
            onComponentUpdate={handleComponentUpdate}
          />
        )}
      </div>
    </div>
  );
}

export default PlatformComponentCarouselOrMosaic;
